// @story HIR-HR11
// @story HIR-HR12
// @story HIR-HR13
// @story HIR-HR14
// @story HIR-HR15
// @story HIR-HR16
// @story HIR-HR17
// @story MSG-SYS10
// CampaignsPage — every message to this school's applicants as a campaign
// (the owner, 28 Oct 2026). The list shows the campaigns people create here
// and the ones Hiring already sends on its own buttons (round dates, fee
// reminders, receipts…), with emails and WhatsApps for each. A campaign is
// three parts — Campaign (name, purpose, its variables), Audience, Messages
// (ticked channels, words with variables) — then Preview, Test, Send, Report.
// Before a send Messages says whether each ticked channel can send now; a
// channel that cannot is blocked with its reason (never swapped for another).
// Replies come back per person, with a link to the conversation in Messages.
//
// The words use Hiring's list of details (30 Oct 2026): "Insert detail" on
// the subject, the email and the WhatsApp words; each detail shows as a chip
// by its plain name (DetailField), WhatsApp words are checked against Meta's
// rules as they are typed, and the preview names anyone with an empty detail
// before Send (the send gate).
//
// Drawn as the other EquaOS apps are (owner, 29 Oct 2026: "make it look
// exactly like the other systems"): the page kit and the design system only.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import RolePicker from '../../components/RolePicker';
import { Button, Card, Checkbox, EmptyState, FormField, Icon, Input, Radio, Select, StatusPill, Switch, Table } from '../../ds';
import { Page, PageHeader, Section, Loading, Note } from '../../components/page';
import { MissedWhatsApp, TellOut } from '../messages/CatchUp';
import DetailField from './DetailField';
import { catalogueOf, keyFor, whatsAppProblems } from './details';

const when = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' })
  : '—');
const errText = (e, fallback) => e?.response?.data?.message || fallback;
const sum = (o = {}) => Object.values(o).reduce((a, b) => a + b, 0);
const STATUS = { draft: 'Draft', sending: 'Sending…', stopped: 'Stopped', done: 'Sent' };
const STATUS_TONE = { draft: 'neutral', sending: 'info', stopped: 'warning', done: 'success' };
const statusPill = (s) => <StatusPill tone={STATUS_TONE[s] || 'neutral'}>{STATUS[s] || s}</StatusPill>;
const PERSON_STATUS = {
  accepted: ['Sent', 'info'], delivered: ['Delivered', 'success'], failed: ['Failed', 'danger'],
  skipped: ['Not sent', 'warning'], planned: ['Waiting', 'neutral'], sending: ['Sending', 'info'],
};

/* A native field on the design system's variables, for what the ds Input
   cannot carry (autofocus). */
const field = {
  width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '0 14px',
  border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
  background: 'var(--surface-1)', color: 'var(--text-1)', font: 'inherit', fontSize: 'var(--fs-body-md)',
};
const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' };
const small = { color: 'var(--text-2)', fontSize: 'var(--fs-body-sm)' };
/* A box inside a card: the preview, the Meta submission, the template. */
const inset = { border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface-2)', padding: 16, display: 'grid', gap: 12 };
const paper = { border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface-1)', padding: 12, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' };
const fields = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 16 });

/* The journey's stages, as the server names them (GET /campaigns/options
   sends the same list; this is only until it has loaded). */
const STAGES = ['Applied', 'Fee paid', 'CV screened', 'Shortlisted', 'In AI assessment', 'In F2F interviews', 'Offered', 'Joined'];
const KIND_CHOICES = [
  { value: 'one_time', label: 'One-time', about: 'Goes once to everyone in the audience, when you press Send.' },
  { value: 'automatic', label: 'Automatic', about: 'Goes by itself to each candidate when they reach a stage, once.' },
  { value: 'reminder', label: 'AI reminder', about: 'Goes again every few days to whoever still fits the audience; the AI can set the rule from your words.' },
];

/* A date box (the design system's Input has no type=date), drawn as one. An
   end date runs to the end of that day in India. */
function DateBox({ id, label, value, end = false, onChange }) {
  const shown = value ? new Date(new Date(value).getTime() + 5.5 * 3600000).toISOString().slice(0, 10) : '';
  return (
    <label htmlFor={id} style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)' }}>
      {label} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
      <input id={id} type="date" value={shown}
        onChange={(e) => onChange(e.target.value ? new Date(`${e.target.value}T${end ? '23:59:59' : '00:00:00'}+05:30`).toISOString() : null)}
        style={{ height: 40, padding: '0 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface-1)', color: 'var(--text-1)', font: 'inherit' }} />
    </label>
  );
}

/* A typed lowest score: empty is "any"; otherwise kept to 0–100. */
const scoreInput = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
};
const linkStyle = { color: 'var(--brand-primary)', fontWeight: 500, textDecoration: 'none' };

/* ── the list ───────────────────────────────────────────────────────────── */
/* The list. `embedded`: inside the Messages page, which has the page's title. */
export function CampaignList({ embedded = false }) {
  const nav = useNavigate();
  const [st, setSt] = useState({ phase: 'loading' });
  const [creating, setCreating] = useState({ open: false, name: '', busy: false, msg: '' });

  useEffect(() => {
    api.get('/campaigns')
      .then((r) => setSt({ phase: 'ready', ...r.data }))
      .catch((e) => setSt({ phase: 'error', msg: errText(e, 'Could not load the communications.') }));
  }, []);

  const create = () => {
    setCreating((c) => ({ ...c, busy: true, msg: '' }));
    api.post('/campaigns', { name: creating.name })
      .then((r) => nav(`/campaigns/${r.data.campaign.id}`))
      .catch((e) => setCreating((c) => ({ ...c, busy: false, msg: errText(e, 'Could not create it.') })));
  };

  const newButton = (
    <Button icon={<Icon name="plus" />} size={embedded ? 'sm' : 'md'} onClick={() => setCreating({ open: true, name: '', busy: false, msg: '' })}>New communication</Button>
  );

  const campaignColumns = [
    { key: 'name', label: 'Communication', render: (c) => (
      <div>
        <div style={{ fontWeight: 500, color: 'var(--text-1)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {c.name}
          {c.system && <StatusPill tone="neutral" dot={false}>{c.system.audience === 'staff' ? 'Built-in · staff' : 'Built-in'}</StatusPill>}
        </div>
        <small style={muted}>{c.system ? `Goes when: ${c.system.event}` : c.purpose}</small>
      </div>
    ) },
    { key: 'status', label: 'Status', render: (c) => ((c.kind || 'one_time') === 'one_time' ? statusPill(c.status)
      : (c.system && !c.system.switch ? <StatusPill tone="info" dot={false}>By hand</StatusPill>
        : (c.active ? <StatusPill tone="success">On</StatusPill> : <StatusPill tone="neutral">Off</StatusPill>))) },
    { key: 'by', label: 'By', render: (c) => c.channels.map((x) => (x === 'email' ? 'Email' : 'WhatsApp')).join(' + ') },
    { key: 'emails', label: 'Emails', render: (c) => <span className="num">{(c.counts.email.accepted || 0) + (c.counts.email.delivered || 0)}</span> },
    { key: 'whatsapps', label: 'WhatsApps', render: (c) => <span className="num">{(c.counts.whatsapp.accepted || 0) + (c.counts.whatsapp.delivered || 0)}</span> },
    { key: 'notSent', label: 'Not sent', render: (c) => <span className="num">{(c.counts.email.failed || 0) + (c.counts.email.skipped || 0) + (c.counts.whatsapp.failed || 0) + (c.counts.whatsapp.skipped || 0)}</span> },
    { key: 'started', label: 'Started', render: (c) => <span style={{ color: 'var(--text-muted)' }}>{when(c.sendStartedAt)}</span> },
  ];
  const builtInColumns = [
    { key: 'label', label: 'Message', render: (m) => <span style={{ color: 'var(--text-1)' }}>{m.label}</span> },
    { key: 'people', label: 'People', render: (m) => <span className="num">{m.people}</span> },
    { key: 'emails', label: 'Emails', render: (m) => <span className="num">{m.emails}</span> },
    { key: 'whatsapps', label: 'WhatsApps', render: (m) => (
      <span><span className="num">{m.whatsapps - m.whatsappsNotDelivered}</span>{m.whatsappsNotDelivered ? <span style={{ color: 'var(--status-warning-text)', fontSize: 'var(--fs-caption)' }}> ({m.whatsappsNotDelivered} not delivered)</span> : null}</span>
    ) },
    { key: 'last', label: 'Last sent', render: (m) => <span style={{ color: 'var(--text-muted)' }}>{when(m.lastAt)}</span> },
    { key: 'who', label: '', render: (m) => <Link style={{ ...linkStyle, fontSize: 'var(--fs-body-sm)' }} to={`/messages?view=sent&message=${encodeURIComponent(m.message)}`}>Who got it</Link> },
  ];

  const body = (
    <>
      {creating.open && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <FormField label={<label htmlFor="campaign-new-name">Name of the communication</label>}>
                <input id="campaign-new-name" autoFocus style={field} value={creating.name} placeholder="e.g. Round 1 dates — November" onChange={(e) => setCreating((c) => ({ ...c, name: e.target.value }))} />
              </FormField>
            </div>
            <Button loading={creating.busy} disabled={creating.busy || !creating.name.trim()} onClick={create}>Create</Button>
            <Button variant="secondary" onClick={() => setCreating({ open: false })}>Cancel</Button>
          </div>
          {creating.msg && <div style={{ marginTop: 12 }}><Note tone="danger">{creating.msg}</Note></div>}
        </Card>
      )}

      {st.phase === 'loading' && <Card><Loading /></Card>}
      {st.phase === 'error' && <Note tone="danger">{st.msg}</Note>}

      {st.phase === 'ready' && (
        <>
          <Section title="Your communications" subtitle={`${st.campaigns.length} communication${st.campaigns.length === 1 ? '' : 's'} · open one to write, preview and send it`}
            // Inside Messages, "New communication" is in the page's own header.
            action={null} padding="20px 0 8px">
            {st.campaigns.length
              ? <Table columns={campaignColumns} rows={st.campaigns} getRowId={(c) => c.id} onRowClick={(c) => nav(`/campaigns/${c.id}`)} />
              : <EmptyState icon={<Icon name="megaphone" />} title="No communications yet" description={'None yet. Press "New communication" to write one.'} />}
          </Section>

          <Section title="Sent by Hiring's own buttons and moments" subtitle="These keep working exactly as before; their whole history is counted here." padding="20px 0 8px">
            {st.builtIn.length
              ? <Table columns={builtInColumns} rows={st.builtIn} getRowId={(m) => m.message} />
              : <EmptyState icon={<Icon name="paper-plane" />} title="Nothing sent yet." description="Hiring's own messages (round dates, fee reminders, receipts) are counted here once they go out." />}
          </Section>
        </>
      )}
    </>
  );

  if (embedded) return body;
  return (
    <Page>
      <PageHeader title="Communications" subtitle="Every message to this school's applicants: who it went to, by email or WhatsApp, and why." actions={newButton} />
      {body}
    </Page>
  );
}

/* ── one of Hiring's own messages (30 Oct 2026) ─────────────────────────────
   It goes at its exact moment from its own place in Hiring; here it has a
   switch, for an applicant's message targeting, and its wording: the
   school's template on each channel, shown in full. */
function SystemCommunication({ c, opts, onSaved }) {
  const [a, setA] = useState(c.audience || { who: 'all' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState('');
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'admin'; } catch { return false; } })();
  const staff = c.system.audience === 'staff';

  const flip = (on) => {
    setBusy('switch');
    api.post(`/campaigns/${c.id}/switch`, { on })
      .then((r) => { onSaved(r.data.campaign); setMsg({ tone: on ? 'success' : 'warning', text: r.data.message }); })
      .catch((e) => setMsg({ tone: 'danger', text: errText(e, 'Could not switch it.') }))
      .finally(() => setBusy(''));
  };
  const save = () => {
    setBusy('save');
    api.put(`/campaigns/${c.id}`, { audience: a })
      .then((r) => { onSaved(r.data.campaign); setMsg({ tone: 'success', text: 'Saved. It goes only to people in this audience from now on.' }); })
      .catch((e) => setMsg({ tone: 'danger', text: errText(e, 'Could not save.') }))
      .finally(() => setBusy(''));
  };

  return (
    <Page>
      <div><Link to="/messages?view=campaigns" style={{ ...linkStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="arrow-left" size={14} /> Back to Messages</Link></div>
      <PageHeader title={c.name}
        subtitle={`One of Hiring's own messages${staff ? ', to staff' : ''}. It goes at its exact moment: ${c.system.event.toLowerCase()}.`}
        actions={!c.system.switch ? <StatusPill tone="info" dot={false}>By hand</StatusPill> : (c.active ? <StatusPill tone="success">On</StatusPill> : <StatusPill tone="neutral">Off</StatusPill>)} />
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}

      {c.system.byHand && <ByHand byHand={c.system.byHand} />}

      {c.system.switch && (
      <Section title="On or off">
        <div style={{ display: 'grid', gap: 10 }}>
          <Switch label={c.active ? 'Hiring sends it' : 'Hiring does not send it'} checked={Boolean(c.active)} disabled={!isAdmin || !!busy} onChange={flip} />
          <div style={small}>{isAdmin ? 'Switched off, Hiring stops sending it at its moment, until it is switched on again.' : 'A recruitment administrator switches it on or off.'}</div>
        </div>
      </Section>

      )}

      {c.system.targeting && (
        <Section title="Who it goes to" subtitle="as any communication; empty is everyone">
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={fields(260)}>
              <Select label="Who" value={a.who || 'all'}
                options={[{ value: 'all', label: 'Every applicant' }, { value: 'paid', label: 'Only applicants who paid the fee' }, { value: 'unpaid', label: 'Only applicants who have not paid yet' }]}
                onChange={(who) => setA({ ...a, who })} />
              <RolePicker label="Role" roles={opts.roles} allOption={{ value: 'all', label: 'Every role' }} value={a.jobId || 'all'}
                onChange={(id) => setA({ ...a, jobId: id === 'all' ? null : id })} />
            </div>
            <div style={{ display: 'flex', gap: '10px 24px', flexWrap: 'wrap' }}>
              {(opts.stages || STAGES).map((st) => {
                const on = (a.stages || []).includes(st);
                return <Checkbox key={st} label={st} checked={on} onChange={() => setA({ ...a, stages: on ? a.stages.filter((x) => x !== st) : [...(a.stages || []), st] })} />;
              })}
            </div>
            <div style={fields(220)}>
              <Input id="sys-min-cv" type="number" label="CV score at least" hint="0 to 100; empty is any" value={a.minCvScore ?? ''} onChange={(e) => setA({ ...a, minCvScore: scoreInput(e.target.value) })} />
              <Input id="sys-min-interview" type="number" label="Interview score at least" hint="their best finished round; empty is any" value={a.minInterviewScore ?? ''} onChange={(e) => setA({ ...a, minInterviewScore: scoreInput(e.target.value) })} />
            </div>
            <div><Button onClick={save} loading={busy === 'save'} disabled={!!busy}>Save who it goes to</Button></div>
          </div>
        </Section>
      )}

      {c.system.gate === 'fee_reminder'
        ? <Section title="Its wording"><div style={small}>Its email and WhatsApp words are shown on its send screen, with the assessment date you type there.</div></Section>
        : <SystemWording gate={c.system.gate} channels={c.channels} />}
      {(c.system.gate === 'submitted' || c.system.gate === 'paid') && <MissedWhatsApp />}
      {c.system.gate === 'rejected' && <TellOut />}
    </Page>
  );
}

/* Sent by hand from the dashboard, exactly as before; here as an extra layer
   (owner, 30 Oct 2026): everyone it has gone to, and the same send screen as
   the dashboard's button. */
function ByHand({ byHand }) {
  const navigate = useNavigate();
  const [h, setH] = useState(null);
  useEffect(() => {
    api.get('/dashboard/messages-sent', { params: { days: 3650 } })
      .then((r) => setH((r.data.messages || []).find((m) => m.message === byHand.history) || { people: 0, emails: 0, whatsapps: 0, whatsappsNotDelivered: 0, lastAt: null }))
      .catch(() => setH({ error: true }));
  }, [byHand.history]);
  return (
    <Section title="Send it" subtitle="it goes when the team presses its button on the dashboard; the same screen opens from here"
      action={<Button icon={<Icon name="paper-plane-right" />} onClick={() => navigate(`/dashboard?open=${byHand.open}`)}>Open it to send</Button>}>
      {!h ? <Loading /> : h.error ? <Note tone="danger">Could not read who it has gone to.</Note> : (
        <div style={{ display: 'flex', gap: '8px 32px', flexWrap: 'wrap', alignItems: 'baseline', fontSize: 14, color: 'var(--text-1)' }}>
          <span><b className="num">{h.people}</b> people</span>
          <span><b className="num">{h.emails}</b> emails</span>
          <span><b className="num">{h.whatsapps - (h.whatsappsNotDelivered || 0)}</b> WhatsApps{h.whatsappsNotDelivered ? ` (${h.whatsappsNotDelivered} not delivered)` : ''}</span>
          <span style={small}>last sent {when(h.lastAt)}</span>
          {h.people > 0 && <Link style={linkStyle} to={`/messages?view=sent&message=${encodeURIComponent(byHand.history)}`}>Who got it</Link>}
        </div>
      )}
    </Section>
  );
}

/* Its wording, here on its own page (owner, 30 Oct 2026: "there should not be a
   separate feature for template"): for each channel it goes on, which of the
   school's templates it uses, and exactly what that template says. The
   templates themselves are the school's, written in the Messages app. */
function SystemWording({ gate, channels }) {
  const [state, setState] = useState({ phase: 'loading' });
  const [pick, setPick] = useState({});
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/school-settings/message-templates')
      .then((r) => {
        const d = r.data.data || {};
        setState({ phase: 'ready', available: d.available || { email: [], whatsapp: [] }, source: d.templatesSource || null });
        const g = (d.gates || {})[gate] || {};
        setPick({ email: g.email || '', wa: g.wa || '', waFallback: g.waFallback || '', savedAt: g.savedAt || null });
      })
      .catch(() => setState({ phase: 'error' }));
  }, [gate]);

  const save = () => {
    setBusy(true);
    setMsg(null);
    api.put(`/school-settings/gates/${gate}`, { email: pick.email, wa: pick.wa, waFallback: pick.waFallback })
      .then((r) => { setPick((p) => ({ ...p, savedAt: r.data.data.savedAt })); setMsg({ tone: 'success', text: 'Saved. It uses this wording from the next one it sends.' }); })
      .catch((e) => setMsg({ tone: 'danger', text: errText(e, 'Could not save the wording.') }))
      .finally(() => setBusy(false));
  };

  if (state.phase === 'loading') return <Section title="Its wording"><Loading /></Section>;
  if (state.phase === 'error') return <Section title="Its wording"><Note tone="danger">Could not load its wording. Try again in a moment.</Note></Section>;

  const app = (() => { const s = (String(window.location.hostname).match(/^hiring\.([a-z0-9-]+)\./) || [])[1]; return s ? `https://communication.${s}.equaos.in` : null; })();
  const lists = state.available;
  const rows = [
    channels.includes('email') && { field: 'email', channel: 'email', label: 'Email', none: 'No email' },
    channels.includes('whatsapp') && { field: 'wa', channel: 'whatsapp', label: 'WhatsApp', none: 'No WhatsApp' },
    channels.includes('whatsapp') && channels.includes('email') && { field: 'waFallback', channel: 'whatsapp', label: 'WhatsApp if that one cannot go', none: 'None' },
  ].filter(Boolean);
  const unreachable = state.source && (!state.source.reached || (state.source.status && state.source.status !== 200));

  return (
    <Section title="Its wording" subtitle={pick.savedAt ? `last changed ${new Date(pick.savedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}` : 'the school\'s own templates'}
      action={<Button onClick={save} loading={busy} disabled={busy}>Save wording</Button>}>
      <div style={{ display: 'grid', gap: 16 }}>
        {msg && <Note tone={msg.tone}>{msg.text}</Note>}
        {unreachable && <Note tone="warning">The school's templates could not be read just now, so only their names show. What is chosen still goes.</Note>}
        {rows.map((row) => {
          const options = lists[row.channel] || [];
          const value = pick[row.field] || '';
          const t = options.find((o) => o.key === value);
          const opts = [{ value: '', label: row.none }, ...options.map((o) => ({ value: o.key, label: o.subject ? `${o.key} — ${o.subject}` : o.key }))];
          if (value && !t) opts.push({ value, label: value });
          return (
            <div key={row.field} style={{ display: 'grid', gap: 8 }}>
              <div style={{ maxWidth: 520 }}>
                <Select label={row.label} value={value} options={opts} onChange={(v) => setPick((p) => ({ ...p, [row.field]: v }))} />
              </div>
              {t && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 16px', display: 'grid', gap: 6, background: 'var(--surface-2)' }}>
                  {row.channel === 'email' && t.subject && <div><span style={small}>Subject: </span><b>{t.subject}</b></div>}
                  {t.body && <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5, color: 'var(--text-1)', maxHeight: 260, overflowY: 'auto' }}>{t.body}</div>}
                  {!t.body && <div style={small}>Its words are not shown here.</div>}
                </div>
              )}
              {value && !t && <div style={small}>Uses “{value}”; its words could not be read just now.</div>}
            </div>
          );
        })}
        <div style={small}>
          The words of a template are written and changed in the{' '}
          {app ? <a href={app} target="_blank" rel="noopener noreferrer" style={linkStyle}>Messages app</a> : 'Messages app'}
          {channels.includes('whatsapp') ? '; a new WhatsApp template goes to Meta for approval there.' : '.'}
        </div>
      </div>
    </Section>
  );
}

/* ── one campaign ───────────────────────────────────────────────────────── */
/* One of the communication's own details: its name and its value, the same
   for everyone. Nobody types a variable: a new one's name in the words is
   made from its label (`fresh`), and one written before keeps its own. */
function OwnDetailRow({ v, i, taken, onChange, onRemove }) {
  const setLabel = (label) => onChange(v.fresh ? { ...v, label, key: keyFor(label, taken) } : { ...v, label });
  return (
    // Wraps onto two lines when narrow, rather than squeezing the boxes into each other.
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <Input id={`campaign-var-${i}-label`} label="Detail" value={v.label} onChange={(e) => setLabel(e.target.value)} placeholder="Assessment date" />
      </div>
      <div style={{ flex: '2 1 280px', minWidth: 0 }}>
        <Input id={`campaign-var-${i}-value`} label="Its value, for everyone" value={v.value} onChange={(e) => onChange({ ...v, value: e.target.value })} placeholder="Saturday, 28th November 2026" />
      </div>
      <Button variant="ghost" title="Remove" aria-label="Remove" icon={<Icon name="x" />} onClick={onRemove} />
    </div>
  );
}

function Report({ id, refreshKey }) {
  const [rep, setRep] = useState(null);
  const [filter, setFilter] = useState('all');
  const load = useCallback(() => api.get(`/campaigns/${id}/report`).then((r) => setRep(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (rep?.campaign?.status !== 'sending') return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [rep?.campaign?.status, load]);
  if (!rep || !rep.people.length) return null;
  const people = rep.people.filter((p) => filter === 'all' || (filter === 'replied' ? p.replies > 0 : filter === 'sent' ? ['accepted', 'delivered'].includes(p.status) : ['failed', 'skipped'].includes(p.status)));
  const replies = rep.replies || [];
  const download = () => {
    const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [['Applicant', 'Channel', 'Status', 'Reason', 'When', 'Replies'].map(cell).join(','), ...rep.people.map((p) => [p.name, p.channel, p.status, p.reason, when(p.at), p.replies || 0].map(cell).join(','))];
    const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = `communication-${rep.campaign.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const tile = (icon, label, o) => (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ color: 'var(--brand-primary)', display: 'inline-flex' }}><Icon name={icon} size={22} /></span>
      <div>
        <div className="num" style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-1)' }}>{(o.accepted || 0) + (o.delivered || 0)}</div>
        <div style={muted}>{label} sent{o.delivered ? `, ${o.delivered} delivered` : ''}{o.failed ? `, ${o.failed} failed` : ''}{o.skipped ? `, ${o.skipped} not sent` : ''}{(o.planned || 0) + (o.sending || 0) ? `, ${(o.planned || 0) + (o.sending || 0)} to go` : ''}</div>
      </div>
    </div>
  );
  const columns = [
    { key: 'name', label: 'Applicant', render: (p) => <span style={{ color: 'var(--text-1)' }}>{p.name}</span> },
    { key: 'channel', label: 'By', render: (p) => (p.channel === 'email' ? 'Email' : 'WhatsApp') },
    { key: 'status', label: 'Status', render: (p) => { const s = PERSON_STATUS[p.status]; return s ? <StatusPill tone={s[1]}>{s[0]}</StatusPill> : p.status; } },
    { key: 'reason', label: 'Why not', render: (p) => p.reason },
    { key: 'at', label: 'When', render: (p) => <span style={{ color: 'var(--text-muted)' }}>{when(p.at)}</span> },
    { key: 'replies', label: 'Replies', render: (p) => <span className="num">{p.replies || ''}</span> },
  ];
  return (
    <Section
      title="Report"
      subtitle={rep.campaign.status === 'sending' ? `sending, ${rep.progress.left} to go` : undefined}
      action={<Button size="sm" variant="secondary" icon={<Icon name="download" />} onClick={download}>Download</Button>}>
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={fields(240)}>
          {tile('paper-plane', 'Emails', rep.counts.email)}
          {tile('chat-circle-text', 'WhatsApps', rep.counts.whatsapp)}
        </div>
        {replies.length > 0 && (
          <div style={{ display: 'grid', gap: 6 }}>
            <div style={muted}>Replies — {rep.replyCounts?.email || 0} by email, {rep.replyCounts?.whatsapp || 0} by WhatsApp. The words are in Messages, where they are answered.</div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 192, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
              {replies.map((x, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', borderTop: i ? '1px solid var(--border)' : 'none', fontSize: 'var(--fs-body-sm)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', color: 'var(--text-1)' }}>
                    <span style={{ color: 'var(--text-muted)', display: 'inline-flex' }}><Icon name="chat-circle-dots" size={14} /></span>{x.name}
                    <span style={{ color: 'var(--text-muted)' }}> · {x.channel === 'email' ? 'Email' : 'WhatsApp'} · {when(x.at)}</span>
                    {x.choice ? <span style={{ color: 'var(--text-2)' }}> · chose “{x.choice}”</span> : null}
                  </span>
                  {x.link
                    ? <a href={x.link} target="_blank" rel="noreferrer" style={{ ...linkStyle, fontSize: 'var(--fs-caption)', flex: 'none' }}>Open in Messages</a>
                    : <span style={{ ...muted, flex: 'none' }}>in the Messages inbox</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {rep.reasons.length > 0 && (
          <div style={{ display: 'grid', gap: 6 }}>
            <div style={muted}>Not sent, and why</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 2, ...small }}>{rep.reasons.map((r) => <li key={`${r.channel}${r.reason}`}>{r.channel === 'email' ? 'Email' : 'WhatsApp'}: {r.reason} — <b>{r.people}</b></li>)}</ul>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['all', 'Everyone'], ['sent', 'Sent'], ['not', 'Not sent'], ...(replies.length ? [['replied', 'Replied']] : [])].map(([k, l]) => (
            <Button key={k} size="sm" variant={filter === k ? 'primary' : 'secondary'} aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</Button>
          ))}
        </div>
        <div style={{ maxHeight: '50vh', overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
          <Table columns={columns} rows={people.map((p, i) => ({ ...p, _row: i }))} getRowId={(p) => p._row} />
        </div>
      </div>
    </Section>
  );
}

function CampaignEditor() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [opts, setOpts] = useState({ roles: [], details: [], personVariables: [], whatsappTemplates: [] });
  // The send gate: leave out the people with an empty detail (chosen after the preview names them).
  const [leaveOut, setLeaveOut] = useState(false);
  // Which real recipient the preview reads as.
  const [sampleAt, setSampleAt] = useState(0);
  const [msg, setMsg] = useState({ text: '', bad: false });
  const [busy, setBusy] = useState('');
  const [pv, setPv] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  // Automatic and reminder ones: who it would go to now, the switch, the AI rule.
  const [dueNow, setDueNow] = useState(null);
  const [includeCurrent, setIncludeCurrent] = useState(false);
  const [ruleText, setRuleText] = useState('');
  // Hyper-personalised email: a sample written for one person.
  const [sample, setSample] = useState(null);
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'admin'; } catch { return false; } })();

  const load = useCallback(() => {
    api.get(`/campaigns/${id}`).then((r) => setC(r.data.campaign)).catch((e) => setMsg({ text: errText(e, 'Could not load the communication.'), bad: true }));
  }, [id]);
  useEffect(() => { load(); api.get('/campaigns/options').then((r) => setOpts(r.data)).catch(() => {}); }, [load]);

  const locked = c?.status === 'sending';
  // Off on the live servers until the Communications writer agent is registered (hub, 30 Oct 2026).
  const automation = opts.automation || { on: false, why: '' };
  const set = (patch) => { setC((x) => ({ ...x, ...patch })); setPv(null); };
  const tpl = useMemo(() => opts.whatsappTemplates.find((t) => t.key === c?.whatsapp?.template), [opts, c]);
  // Hiring's list of details and this communication's own (MSG-SYS10).
  const details = useMemo(() => catalogueOf(opts.details || opts.personVariables || [], c?.variables || []), [opts, c?.variables]);
  const waProblems = useMemo(() => whatsAppProblems(c?.whatsapp?.text, details), [c?.whatsapp?.text, details]);

  const save = () => {
    setBusy('save');
    return api.put(`/campaigns/${id}`, { name: c.name, purpose: c.purpose, variables: c.variables, audience: c.audience, channels: c.channels, email: c.email, whatsapp: c.whatsapp, kind: c.kind, startsAt: c.startsAt || null, endsAt: c.endsAt || null, trigger: c.trigger, reminder: c.reminder })
      .then((r) => { setC(r.data.campaign); setMsg({ text: 'Saved.', bad: false }); return true; })
      .catch((e) => { setMsg({ text: errText(e, 'Could not save.'), bad: true }); return false; })
      .finally(() => setBusy(''));
  };
  /* Write with AI (owner, 29 Oct 2026): saved first, so the AI writes from
     the purpose and variables as they are on screen; the draft fills the
     fields to edit, and nothing is sent. For WhatsApp it picks an approved
     template that fits, or writes the communication's own words for Meta. */
  const writeWithAI = async (channel) => {
    if (!(await save())) return;
    setBusy(`ai-${channel}`);
    api.post(`/campaigns/${id}/write`, { channel })
      .then((r) => {
        if (channel === 'email') {
          set({ email: { ...c.email, ...r.data.email } });
          setMsg({ text: 'The AI wrote the email. Read it, change anything, then Save.', bad: false });
        } else {
          const w = r.data.whatsapp;
          set({ whatsapp: { ...c.whatsapp, mode: w.mode, template: w.template, params: w.params, text: w.text } });
          const rules = (w.problems || []).length ? ` Fix this first: ${w.problems.join(' ')}` : '';
          setMsg({
            text: w.mode === 'template'
              ? `The AI chose the approved template "${w.template}". ${w.reason} Check it, then Save.`
              : `No approved template fits, so the AI wrote the WhatsApp words. ${w.reason} Meta approves them before they can be sent.${rules}`,
            bad: Boolean(rules),
          });
        }
      })
      .catch((e) => setMsg({ text: errText(e, 'The AI could not write it just now.'), bad: true }))
      .finally(() => setBusy(''));
  };
  /* A sample of the hyper-personalised email, for one person; nothing is sent. */
  const showSample = async () => {
    if (!(await save())) return;
    setBusy('sample');
    api.post(`/campaigns/${id}/personal-sample`).then((r) => setSample(r.data)).catch((e) => setMsg({ text: errText(e, 'The AI could not write a sample just now.'), bad: true })).finally(() => setBusy(''));
  };
  /* Who it would go to right now: nothing is sent. */
  const showDue = async () => {
    if (!(await save())) return;
    setBusy('due');
    api.get(`/campaigns/${id}/due`).then((r) => setDueNow(r.data)).catch((e) => setMsg({ text: errText(e, 'Could not work it out.'), bad: true })).finally(() => setBusy(''));
  };
  /* Switch an automatic or reminder communication on or off (a recruitment administrator). */
  const doSwitch = async (on) => {
    if (on && !(await save())) return;
    setBusy('switch');
    api.post(`/campaigns/${id}/switch`, { on, includeCurrent })
      .then((r) => { setC(r.data.campaign); setMsg({ text: r.data.message, bad: false }); })
      .catch((e) => setMsg({ text: errText(e, 'Could not switch it.'), bad: true }))
      .finally(() => setBusy(''));
  };
  /* The AI reads the reminder rule from the team's words and fills the form. */
  const readRule = () => {
    setBusy('rule');
    api.post(`/campaigns/${id}/plan-rule`, { text: ruleText })
      .then((r) => {
        const rule = r.data.rule;
        set({ audience: { ...c.audience, ...rule.audience }, reminder: rule.reminder });
        setMsg({ text: `The AI read the rule: ${rule.reminder.summary} Check the audience and timing below, then Save.`, bad: false });
      })
      .catch((e) => setMsg({ text: errText(e, 'The AI could not read the rule.'), bad: true }))
      .finally(() => setBusy(''));
  };
  const doPreview = async () => {
    if (!(await save())) return;
    setBusy('preview');
    api.post(`/campaigns/${id}/preview`).then((r) => { setPv(r.data); setLeaveOut(false); setSampleAt(0); }).catch((e) => setMsg({ text: errText(e, 'Could not prepare the preview.'), bad: true })).finally(() => setBusy(''));
  };
  const doTest = () => {
    const to = testTo.split(/[,;]+/).map((x) => x.trim()).filter(Boolean).map((x) => (x.includes('@') ? { email: x, name: x.split('@')[0].replace(/[._]/g, ' ') } : { phone: x.replace(/[^\d+]/g, ''), name: 'Colleague' }));
    setBusy('test');
    api.post(`/campaigns/${id}/test`, { to }).then((r) => setMsg({ text: r.data.message + r.data.sent.map((s) => (s.problems.length ? ` ${s.to}: ${s.problems.join('; ')}.` : '')).join(''), bad: false })).catch((e) => setMsg({ text: errText(e, 'The test could not be sent.'), bad: true })).finally(() => setBusy(''));
  };
  const doSend = () => {
    setBusy('send');
    api.post(`/campaigns/${id}/send`, { leaveOut }).then((r) => { setMsg({ text: r.data.message, bad: false }); setPv(null); load(); setRefreshKey((k) => k + 1); }).catch((e) => setMsg({ text: errText(e, 'Could not start sending. Nothing was sent.'), bad: true })).finally(() => setBusy(''));
  };
  const doStop = () => {
    api.post(`/campaigns/${id}/stop`).then((r) => { setMsg({ text: r.data.message, bad: false }); setTimeout(() => { load(); setRefreshKey((k) => k + 1); }, 2000); });
  };
  // The campaign's own WhatsApp words, to Meta for approval (HIR-HR18).
  const [waSub, setWaSub] = useState(null);
  const openSubmit = async () => {
    if (!(await save())) return;
    api.get(`/campaigns/${id}/whatsapp/submission`).then((r) => setWaSub(r.data)).catch((e) => setMsg({ text: errText(e, 'Could not prepare the submission.'), bad: true }));
  };
  const doSubmit = () => {
    setBusy('wa');
    api.post(`/campaigns/${id}/whatsapp/submission`)
      .then((r) => { setMsg({ text: r.data.message, bad: false }); setWaSub(null); load(); api.get('/campaigns/options').then((o) => setOpts(o.data)).catch(() => {}); })
      .catch((e) => setMsg({ text: errText(e, 'Could not submit it to Meta.'), bad: true }))
      .finally(() => setBusy(''));
  };
  // Where the campaign's own WhatsApp words stand with Meta; `tone` is a status colour.
  const ownStatus = () => {
    const sub = c.whatsapp.submitted;
    if (!sub) return { text: 'Not submitted to Meta yet.', tone: 'muted' };
    if (sub.text !== c.whatsapp.text) return { text: 'Changed since it was submitted: submit it again.', tone: 'warning' };
    const t = opts.whatsappTemplates.find((x) => x.key === sub.key);
    if (t?.approved) return { text: `Approved by Meta (${sub.key}).`, tone: 'success' };
    if (t?.status === 'rejected') return { text: `Rejected by Meta${t.rejectedReason ? `: ${t.rejectedReason}` : ''}. Change the words and submit again.`, tone: 'danger' };
    return { text: `Waiting for Meta's approval (submitted ${when(sub.at)}${sub.by ? ` by ${sub.by}` : ''}). Usually minutes to a day.`, tone: 'warning' };
  };

  if (!c) {
    return (
      <Page>
        {msg.text ? <Note tone={msg.bad ? 'danger' : 'info'}>{msg.text}</Note> : <Card><Loading /></Card>}
      </Page>
    );
  }
  if (c.system) return <SystemCommunication c={c} opts={opts} onSaved={setC} />;
  const toggle = (ch) => set({ channels: c.channels.includes(ch) ? c.channels.filter((x) => x !== ch) : ['email', 'whatsapp'].filter((x) => x === ch || c.channels.includes(x)) });
  const own = c.channels.includes('whatsapp') && c.whatsapp.mode === 'own' ? ownStatus() : null;
  const detailOptions = details.map((d) => ({ value: d.key, label: d.label }));
  const ownKeysTaken = (i) => [...(opts.details || []).map((d) => d.key), ...c.variables.filter((_, j) => j !== i).map((v) => v.key)];

  return (
    <Page>
      <div>
        <Link to="/messages?view=campaigns" style={{ ...linkStyle, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
          <Icon name="arrow-left" size={14} /> Back to Messages
        </Link>
      </div>
      <PageHeader
        title={c.name || 'Communication'}
        subtitle={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{statusPill(c.status)}{c.sentBy ? <span>sent by {c.sentBy}</span> : null}</span>}
      />

      {/* While it sends, the words and the audience cannot change. */}
      <fieldset disabled={locked} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, display: 'grid', gap: 24 }}>
        <Section title="1. Communication" subtitle="its kind, name, dates, what it is for, and its own details">
          <div style={{ display: 'grid', gap: 20 }}>
            {/* Three kinds (owner, 29 Oct 2026). Fixed once it has gone out. */}
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 500 }}>Kind</div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {KIND_CHOICES.map((k) => (
                  <Radio key={k.value} name="campaign-kind" label={k.label} checked={(c.kind || 'one_time') === k.value}
                    disabled={locked || c.active} onChange={() => { set({ kind: k.value }); setDueNow(null); }} />
                ))}
              </div>
              <div style={small}>{KIND_CHOICES.find((k) => k.value === (c.kind || 'one_time')).about}</div>
            </div>
            <div style={fields(220)}>
              <DateBox id="campaign-starts" label="Starts on" value={c.startsAt} onChange={(v) => set({ startsAt: v })} />
              <DateBox id="campaign-ends" label="Ends on" value={c.endsAt} end onChange={(v) => set({ endsAt: v })} />
            </div>
            <div style={fields(260)}>
              <Input id="campaign-name" label="Name" value={c.name} onChange={(e) => set({ name: e.target.value })} />
              <Input id="campaign-purpose" label="What it is for" value={c.purpose} onChange={(e) => set({ purpose: e.target.value })} placeholder="Tell applicants the assessment and interview dates" />
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={small}>This communication's own details: things you fill in once, the same for everyone (a date, a venue, who signs). Each person's own details (first name, role, test link, interview time…) are filled in for you from "Insert detail".</div>
              {c.variables.map((v, i) => (
                <OwnDetailRow key={i} i={i} v={v} taken={ownKeysTaken(i)} onChange={(nv) => set({ variables: c.variables.map((x, j) => (j === i ? nv : x)) })} onRemove={() => set({ variables: c.variables.filter((_, j) => j !== i) })} />
              ))}
              <div><Button size="sm" variant="secondary" icon={<Icon name="plus" />} onClick={() => set({ variables: [...c.variables, { key: keyFor('detail', ownKeysTaken(-1)), label: '', value: '', fresh: true }] })}>Add a detail</Button></div>
            </div>
          </div>
        </Section>

        <Section title="2. Audience" subtitle="one message per person">
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={fields(260)}>
              <Select label="Who" value={c.audience.who} disabled={locked}
                options={[{ value: 'all', label: 'Every applicant from the careers page' }, { value: 'paid', label: 'Only applicants who paid the fee' }, { value: 'unpaid', label: 'Only applicants who have not paid yet' }]}
                onChange={(who) => set({ audience: { ...c.audience, who } })} />
              {/* Type to find a role (29 Oct 2026). */}
              <RolePicker label="Role" roles={opts.roles} allOption={{ value: 'all', label: 'Every role' }} disabled={locked}
                value={c.audience.jobId || 'all'}
                onChange={(id) => set({ audience: { ...c.audience, jobId: id === 'all' ? null : id } })} />
            </div>

            {/* Narrower (owner, 29 Oct 2026): where they are in the journey, and
                how they scored. Each applies only when set. */}
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)' }}>
                Stage <span style={muted}>· tick the stages to include; none ticked is every stage</span>
              </div>
              <div style={{ display: 'flex', gap: '10px 24px', flexWrap: 'wrap' }}>
                {(opts.stages || STAGES).map((s) => {
                  const on = (c.audience.stages || []).includes(s);
                  return (
                    <Checkbox key={s} label={s} checked={on} disabled={locked}
                      onChange={() => set({ audience: { ...c.audience, stages: on ? c.audience.stages.filter((x) => x !== s) : [...(c.audience.stages || []), s] } })} />
                  );
                })}
              </div>
            </div>
            <div style={fields(220)}>
              <Input id="campaign-min-cv" type="number" label="CV score at least" hint="0 to 100; empty is any"
                value={c.audience.minCvScore ?? ''} disabled={locked}
                onChange={(e) => set({ audience: { ...c.audience, minCvScore: scoreInput(e.target.value) } })} />
              <Input id="campaign-min-interview" type="number" label="Interview score at least" hint="their best finished round; empty is any"
                value={c.audience.minInterviewScore ?? ''} disabled={locked}
                onChange={(e) => set({ audience: { ...c.audience, minInterviewScore: scoreInput(e.target.value) } })} />
            </div>
            <p style={{ margin: 0, ...muted }}>One message per person, even with several applications. Preview shows the count and the list.</p>
          </div>
        </Section>

        {(c.kind || 'one_time') === 'automatic' && (
          <Section title="When it goes" subtitle="by itself, when a candidate moves from one stage to another">
            <div style={{ display: 'grid', gap: 12 }}>
              {/* Which stage to which stage (owner, 29 Oct 2026): the only trigger. */}
              <div style={fields(240)}>
                <Select label="When a candidate moves from" value={c.trigger?.fromStage || ''} disabled={locked || c.active}
                  options={[{ value: '', label: 'Any stage (or a new application)' }, ...(opts.stages || STAGES).map((x) => ({ value: x, label: x }))]}
                  onChange={(fromStage) => set({ trigger: { ...c.trigger, fromStage } })} />
                <Select label="To" value={c.trigger?.stage || ''} disabled={locked || c.active}
                  options={[{ value: '', label: 'Choose a stage' }, ...(opts.stages || STAGES).map((x) => ({ value: x, label: x }))]}
                  onChange={(stage) => set({ trigger: { ...c.trigger, stage } })} />
              </div>
              <Checkbox label="Also send to those already at the 'to' stage when it is switched on" checked={includeCurrent} disabled={c.active} onChange={setIncludeCurrent} />
              <div style={small}>Only moves after it is switched on count. Each candidate gets it once. The audience above narrows it further (a role, scores).</div>
            </div>
          </Section>
        )}
        {(c.kind || 'one_time') === 'reminder' && (
          <Section title="When it goes" subtitle="again and again, until they no longer fit the audience">
            <div style={{ display: 'grid', gap: 14 }}>
              <Input id="campaign-rule" label="Describe the reminder in your own words" multiline rows={3} value={ruleText}
                placeholder="e.g. Remind applicants who have not paid the fee every 10 days until they pay, at most 3 times"
                onChange={(e) => setRuleText(e.target.value)} />
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="secondary" icon={<Icon name="sparkle" />} loading={busy === 'rule'} disabled={!!busy || !ruleText.trim() || c.active} onClick={readRule}>Set it up with AI</Button>
                <span style={muted}>The AI sets the audience and the timing below from your words; you check them.</span>
              </div>
              {c.reminder?.summary && <Note tone="info">{c.reminder.summary}</Note>}
              <div style={fields(200)}>
                <Input id="campaign-every" type="number" label="Every how many days" hint="1 to 60" value={c.reminder?.everyDays ?? 7} disabled={c.active}
                  onChange={(e) => set({ reminder: { ...c.reminder, everyDays: Math.min(60, Math.max(1, Math.round(Number(e.target.value) || 1))) } })} />
                <Input id="campaign-most" type="number" label="The most times anyone gets it" hint="1 to 10" value={c.reminder?.maxTimes ?? 3} disabled={c.active}
                  onChange={(e) => set({ reminder: { ...c.reminder, maxTimes: Math.min(10, Math.max(1, Math.round(Number(e.target.value) || 1))) } })} />
              </div>
              <div style={small}>A person stops getting it as soon as they no longer fit the audience above (for example, once they pay), or after the most times.</div>
            </div>
          </Section>
        )}

        <Section title="3. Messages" subtitle="tick the channels, then write the words">
          <div style={{ display: 'grid', gap: 20 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <Checkbox label="Email" checked={c.channels.includes('email')} disabled={locked} onChange={() => toggle('email')} />
              <Checkbox label="WhatsApp" checked={c.channels.includes('whatsapp')} disabled={locked} onChange={() => toggle('whatsapp')} />
            </div>
            {c.channels.includes('email') && (
              <div style={{ display: 'grid', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <Button variant="secondary" icon={<Icon name="sparkle" />} disabled={locked || !!busy} loading={busy === 'ai-email'} onClick={() => writeWithAI('email')}>Write the email with AI</Button>
                  <span style={muted}>From what it is for and its variables, in the school's <Link to="/settings?tab=voice" style={{ color: 'var(--brand-primary-ink)' }}>brand guideline</Link>. You edit it before anything is sent.</span>
                </div>
                {/* Hyper-personalised (owner, 29 Oct 2026): email only; WhatsApp is an approved template. */}
                <div style={{ ...inset, gap: 10 }}>
                  <Switch label="Hyper-personalised: the AI writes each person's own email as it goes" checked={Boolean(c.email.personalized)} disabled={locked || c.active || (!automation.on && !c.email.personalized)}
                    onChange={(on) => { set({ email: { ...c.email, personalized: on } }); setSample(null); }} />
                  <div style={small}>
                    Your email below is the guide: everything it says, its dates and details, stays. The AI makes it each person's own, from their first name, the role they applied for, where they are now and what comes next for them, in the brand guideline. If the AI cannot write someone's, they get your email as written. One AI call per email.
                  </div>
                  {!automation.on && <Note tone="info">{automation.why}</Note>}
                  {c.email.personalized && (
                    <div><Button size="sm" variant="secondary" icon={<Icon name="sparkle" />} loading={busy === 'sample'} disabled={!!busy} onClick={showSample}>See a sample for one person</Button></div>
                  )}
                  {sample && c.email.personalized && (
                    <div style={{ display: 'grid', gap: 6, background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 14 }}>
                      <div style={muted}>For {sample.forName}</div>
                      <div style={{ fontWeight: 600 }}>{sample.email.subject}</div>
                      <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--fs-body-sm)', color: 'var(--text-1)' }}>{sample.email.text}</div>
                    </div>
                  )}
                </div>
                <DetailField id="campaign-email-subject" label="Email subject" multiline={false} details={details} disabled={locked}
                  value={c.email.subject} onChange={(subject) => set({ email: { ...c.email, subject } })} />
                <DetailField id="campaign-email-text" label="Email (a blank line starts a new paragraph)" rows={10} details={details} disabled={locked}
                  value={c.email.text} onChange={(text) => set({ email: { ...c.email, text } })}
                  placeholder={'Dear [First name],\n\nThank you for applying…'} />
              </div>
            )}
            {c.channels.includes('whatsapp') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Button variant="secondary" icon={<Icon name="sparkle" />} disabled={locked || !!busy} loading={busy === 'ai-whatsapp'} onClick={() => writeWithAI('whatsapp')}>Choose or write the WhatsApp with AI</Button>
                <span style={muted}>Picks the approved template that fits; when none does, writes the words for Meta to approve.</span>
              </div>
            )}
            {c.channels.includes('whatsapp') && (
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <Radio name="campaign-wa-mode" label="Use an approved template" checked={c.whatsapp.mode !== 'own'} disabled={locked} onChange={() => set({ whatsapp: { ...c.whatsapp, mode: 'template' } })} />
                <Radio name="campaign-wa-mode" label="Write our own (Meta approves it first)" checked={c.whatsapp.mode === 'own'} disabled={locked} onChange={() => set({ whatsapp: { ...c.whatsapp, mode: 'own' } })} />
              </div>
            )}
            {own && (
              <div style={{ display: 'grid', gap: 12 }}>
                <DetailField id="campaign-wa-text" label="WhatsApp message (one paragraph works best; Meta checks the words)" rows={5} details={details} disabled={locked}
                  value={c.whatsapp.text} onChange={(text) => set({ whatsapp: { ...c.whatsapp, text } })}
                  placeholder="Dear [First name], your assessment is on [Assessment date]. Please be on time. HR team, [School name]"
                  hint="Hiring numbers the details for Meta and sends each one's example as the sample." />
                {waProblems.length > 0 && (
                  <Note tone="warning">
                    <div style={{ display: 'grid', gap: 4 }}>
                      <b>Meta would refuse these words:</b>
                      {waProblems.map((p) => <div key={p}>{p}</div>)}
                    </div>
                  </Note>
                )}
                <div style={{ fontSize: 'var(--fs-body-sm)', color: own.tone === 'muted' ? 'var(--text-muted)' : `var(--status-${own.tone}-text)` }}>{own.text}</div>
                <div><Button variant="secondary" disabled={locked || !!busy || !String(c.whatsapp.text || '').trim() || waProblems.length > 0} onClick={openSubmit}>Submit to Meta for approval…</Button></div>
                {waSub && (
                  <div style={inset}>
                    <div style={{ fontWeight: 500, color: 'var(--text-1)', fontSize: 'var(--fs-body-sm)' }}>This goes to Meta, from the school's WhatsApp account:</div>
                    <div style={{ ...paper, whiteSpace: 'pre-wrap' }}>{waSub.body}</div>
                    {(waSub.numbered || []).length > 0 && (
                      <div style={{ ...small, display: 'grid', gap: 2 }}>
                        {waSub.numbered.map((x) => <div key={x.n}><span className="num">{`{{${x.n}}}`}</span> is {x.label}, sample “{x.sample}”</div>)}
                      </div>
                    )}
                    <div style={small}>Example Meta sees: {waSub.reads}</div>
                    <div style={small}>Type: {waSub.category === 'UTILITY' ? 'Utility (information about their application)' : waSub.category} · name {waSub.key}</div>
                    <div style={muted}>{waSub.note}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button loading={busy === 'wa'} disabled={!!busy} onClick={doSubmit}>Submit to Meta</Button>
                      <Button variant="secondary" onClick={() => setWaSub(null)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {c.channels.includes('whatsapp') && c.whatsapp.mode !== 'own' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <Select label="WhatsApp template (WhatsApp needs a template Meta has approved)" value={c.whatsapp.template || ''} disabled={locked}
                  placeholder="Choose…"
                  options={[{ value: '', label: 'Choose…' }, ...opts.whatsappTemplates.filter((t) => !t.own).map((t) => ({ value: t.key, label: `${t.key}${t.approved ? '' : ' — not approved yet'}` }))]}
                  onChange={(key) => {
                    const t = opts.whatsappTemplates.find((x) => x.key === key);
                    set({ whatsapp: { ...c.whatsapp, template: key, params: t ? Array.from({ length: t.blanks }, (_, i) => c.whatsapp.params[i] || '') : [] } });
                  }} />
                {tpl && (
                  <>
                    <div style={{ ...paper, background: 'var(--surface-2)', whiteSpace: 'pre-wrap' }}>{tpl.body}</div>
                    <div style={fields(200)}>
                      {Array.from({ length: tpl.blanks }, (_, i) => (
                        <Select key={i} label={`Place ${i + 1}${tpl.suggested[i] ? ` (was ${tpl.suggested[i]})` : ''}`} disabled={locked}
                          value={c.whatsapp.params[i] || ''} placeholder="Choose a detail…"
                          options={[{ value: '', label: 'Choose a detail…' }, ...detailOptions]}
                          onChange={(val) => set({ whatsapp: { ...c.whatsapp, params: Array.from({ length: tpl.blanks }, (_, j) => (j === i ? val : c.whatsapp.params[j] || '')) } })} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Section>
      </fieldset>

      <Card>
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="secondary" loading={busy === 'save'} disabled={locked || !!busy} onClick={save}>Save</Button>
            <Button loading={busy === 'preview'} disabled={locked || !!busy} onClick={doPreview}>Preview</Button>
            {locked && <Button variant="danger" icon={<Icon name="x" />} onClick={doStop}>Stop sending</Button>}
            {(c.kind || 'one_time') !== 'one_time' && (
              <>
                <Button variant="secondary" loading={busy === 'due'} disabled={!!busy} onClick={showDue}>Who it would go to now</Button>
                {isAdmin && (c.active
                  ? <Button variant="danger" loading={busy === 'switch'} disabled={!!busy} onClick={() => doSwitch(false)}>Switch off</Button>
                  : <Button loading={busy === 'switch'} disabled={!!busy || !automation.on} icon={<Icon name="check-circle" />} onClick={() => doSwitch(true)}>Switch on</Button>)}
              </>
            )}
          </div>
          {(c.kind || 'one_time') !== 'one_time' && !automation.on && <Note tone="info">{automation.why}</Note>}
          {(c.kind || 'one_time') !== 'one_time' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-body-sm)' }}>
              {c.active ? <StatusPill tone="success">On</StatusPill> : <StatusPill tone="neutral">Off</StatusPill>}
              <span style={muted}>
                {c.active
                  ? `Goes by itself${c.switchedOn ? `, switched on by ${c.switchedOn.by || 'someone'} on ${new Date(c.switchedOn.at).toLocaleDateString('en-IN')}` : ''}. The school's pause stops it too.`
                  : (isAdmin ? 'Nothing goes until you switch it on.' : 'Nothing goes until a recruitment administrator switches it on.')}
              </span>
            </div>
          )}
          {dueNow && (
            <div style={inset}>
              <div style={{ fontWeight: 500 }}>{dueNow.why || `Right now it would go to ${dueNow.count} ${dueNow.count === 1 ? 'person' : 'people'}${dueNow.active ? '' : ' (once switched on)'}.`}</div>
              {dueNow.people.length > 0 && (
                <Table columns={[
                  { key: 'name', label: 'Applicant', render: (p) => p.name },
                  { key: 'role', label: 'Role', render: (p) => p.role || '—' },
                  { key: 'round', label: (c.kind === 'automatic' ? 'Moved' : 'Time'), render: (p) => (c.kind === 'automatic' ? `${p.movedFrom === '(new)' ? 'new application' : p.movedFrom || '—'} → ${c.trigger?.stage}` : (p.round > 1 ? `reminder ${p.round}` : 'first')) },
                  { key: 'to', label: 'To', render: (p) => [p.email, p.phone].filter(Boolean).join(' · ') },
                ]} rows={dueNow.people} getRowId={(p) => `${p.name}|${p.email}|${p.phone}|${p.round}`} />
              )}
              {dueNow.isPartial && <div style={small}>The first 100 are shown.</div>}
            </div>
          )}
          {msg.text && <Note tone={msg.bad ? 'danger' : 'info'}>{msg.text}</Note>}

          {pv && (
            <div style={inset}>
              {pv.problems.length > 0 && (
                <Note tone="warning">{pv.problems.map((p) => <div key={p} style={{ display: 'flex', gap: 6 }}><Icon name="warning-circle" size={14} style={{ flex: 'none', marginTop: 2 }} /> {p}</div>)}</Note>
              )}
              {pv.held && <Note tone="warning">{pv.heldMessage}</Note>}
              {pv.channelHealth && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(pv.channelHealth).map(([ch, h]) => (
                    <StatusPill key={ch} tone={h.ok ? 'success' : 'danger'}>{ch === 'email' ? 'Email' : 'WhatsApp'}: {h.ok ? 'can send now' : 'cannot send now'}</StatusPill>
                  ))}
                </div>
              )}
              <div style={{ color: 'var(--text-1)', fontSize: 'var(--fs-body-md)' }}>
                Goes to <b>{pv.counts.people}</b> {pv.counts.people === 1 ? 'person' : 'people'}: {pv.counts.email ? `${pv.counts.email} by email` : ''}{pv.counts.email && pv.counts.whatsapp ? ', ' : ''}{pv.counts.whatsapp ? `${pv.counts.whatsapp} by WhatsApp` : ''}.
                {pv.counts.alreadySent ? <span style={{ color: 'var(--text-2)' }}> {pv.counts.alreadySent} already had it and are skipped.</span> : null}
                {pv.counts.cannotReach ? <span style={{ color: 'var(--text-2)' }}> {pv.counts.cannotReach} cannot be reached that way (listed below).</span> : null}
              </div>
              {(pv.gaps || []).length > 0 && (
                <div style={{ ...paper, display: 'grid', gap: 8, borderColor: 'var(--status-warning-text)' }}>
                  <div style={{ color: 'var(--text-1)', fontWeight: 500 }}>
                    {pv.counts.withGaps} {pv.counts.withGaps === 1 ? 'person has' : 'people have'} an empty detail the message uses, so it cannot go to them as written:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 2, maxHeight: 160, overflow: 'auto' }}>
                    {pv.gaps.map((g, i) => <li key={i}>{g.name}{g.role ? <span style={{ color: 'var(--text-muted)' }}> · {g.role}</span> : null} — no {g.missing.join(', no ')}</li>)}
                  </ul>
                  <div style={small}>Fill in their details (for example give them an interview slot), then Preview again; or leave them out of this send. Anyone left out can get it in a later send.</div>
                  <Checkbox label={`Leave ${pv.counts.withGaps === 1 ? 'this person' : `these ${pv.counts.withGaps} people`} out and send to the others`} checked={leaveOut} disabled={!pv.readyIfLeftOut} onChange={setLeaveOut} />
                </div>
              )}
              {(() => {
                const shownMessage = (pv.samples || [])[sampleAt] || pv.message;
                if (!shownMessage) return null;
                const names = (pv.samples || []).map((x, i) => ({ value: String(i), label: x.forName }));
                return (
                  <>
                    {names.length > 1 && (
                      <div style={{ maxWidth: 320 }}>
                        <Select label="Read it as" value={String(sampleAt)} options={names} onChange={(v) => setSampleAt(Number(v))} />
                      </div>
                    )}
                    {shownMessage.email && (
                      <div style={paper}>
                        <div style={muted}>Email, as {shownMessage.forName} will read it</div>
                        <div style={{ fontWeight: 500, color: 'var(--text-1)', marginTop: 4 }}>Subject: {shownMessage.email.subject}</div>
                        <div style={{ whiteSpace: 'pre-wrap', marginTop: 4, maxHeight: 288, overflow: 'auto' }}>{shownMessage.email.text}</div>
                      </div>
                    )}
                    {shownMessage.whatsapp && (
                      <div style={paper}>
                        <div style={muted}>WhatsApp, as {shownMessage.forName} will read it</div>
                        <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{shownMessage.whatsapp.text}</div>
                      </div>
                    )}
                  </>
                );
              })()}
              <details style={small}><summary style={{ cursor: 'pointer' }}>Who gets it ({pv.recipients.length}{pv.counts.people > pv.recipients.length ? ` of ${pv.counts.people}` : ''})</summary>
                <div style={{ ...paper, padding: 0, marginTop: 6, maxHeight: 160, overflow: 'auto' }}>
                  {pv.recipients.map((r, i) => (
                    <div key={i} style={{ display: 'grid', gap: 2, padding: '6px 10px', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ color: 'var(--text-1)' }}>{r.name}{r.role ? <span style={{ color: 'var(--text-muted)' }}> · {r.role}</span> : null}</span>
                        <span className="num" style={{ color: 'var(--text-muted)' }}>{[r.email, r.phone].filter(Boolean).join(' · ')}</span>
                      </div>
                      {(pv.detailsUsed || []).length > 0 && (
                        <div style={{ color: 'var(--text-2)', fontSize: 'var(--fs-caption)' }}>
                          {pv.detailsUsed.map((d) => {
                            const v = r.values?.[d.key];
                            return <span key={d.key} style={{ marginRight: 12 }}>{d.label}: {v ? v : <b style={{ color: 'var(--status-warning-text)' }}>empty</b>}</span>;
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
              {pv.cannotReach.length > 0 && (
                <details style={small}><summary style={{ cursor: 'pointer' }}>Cannot be reached ({pv.cannotReach.length}) — for the office to contact</summary>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18, display: 'grid', gap: 2 }}>{pv.cannotReach.map((x, i) => <li key={i}>{x.name} — {x.reason}</li>)}</ul>
                </details>
              )}
              <div style={{ ...paper, display: 'grid', gap: 10 }}>
                <Input id="campaign-test-to" label="Send a test first, to staff only (internal emails or staff test mobiles, separated by commas)"
                  value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="name@equaos.in, +91 98xxx xxxxx" />
                <div><Button size="sm" variant="secondary" disabled={!!busy || !testTo.trim() || pv.problems.length > (pv.blocked || []).length + ((pv.gaps || []).length ? 1 : 0)} onClick={doTest}>{busy === 'test' ? 'Sending the test…' : 'Send a test'}</Button></div>
              </div>
              {/* The send itself: only after the preview above, and only when it says ready.
                  Automatic and reminder ones are switched on instead. */}
              <div style={{ display: (c.kind || 'one_time') === 'one_time' ? undefined : 'none' }}>
                <Button loading={busy === 'send'} icon={<Icon name="paper-plane-right" />} disabled={!(pv.ready || (leaveOut && pv.readyIfLeftOut)) || !!busy} onClick={doSend}>
                  Send by {c.channels.map((x) => (x === 'email' ? 'email' : 'WhatsApp')).join(' and ')} to {leaveOut ? pv.counts.people - (pv.counts.withGaps || 0) : pv.counts.people}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Report id={id} refreshKey={refreshKey} />
    </Page>
  );
}

export default function CampaignsPage() {
  const { id } = useParams();
  return id ? <CampaignEditor /> : <CampaignList />;
}

export { sum };
